import { useLanguage } from '@/i18n';
import { KeenIcon } from '@/components';
import { Menu, MenuItem, MenuSub, MenuToggle } from '@/components/menu';
import { Tab, TabPanel, Tabs, TabsList } from '@/components/tabs';
import { DropdownCrud2 } from '@/partials/dropdowns/general';
import { DropdownNotificationsAll } from './DropdownNotificationsAll';
import { DropdownNotificationsInbox } from './DropdownNotificationsInbox';
import { DropdownNotificationsTeam } from './DropdownNotificationsTeam';
import { DropdownNotificationsFollowing } from './DropdownNotificationsFollowing';
import notificationService from '@/services/notification/notificationService';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { useSocket } from '@/providers/SocketProvider';
import { DefaultTooltip } from '@/components';
import clsx from 'clsx';
import { useAuthContext } from '@/auth';
import { useAbility } from '@/providers';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

const DropdownNotifications = ({ menuTtemRef }) => {
  const { auth } = useAuthContext();
  const { isRTL } = useLanguage();
  const { notifications, pushInitialNotifications } = useSocket();
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // The permission gate lives here rather than at the call sites. Callers invoke
  // this as a plain function, so its hooks join theirs — skipping the call on a
  // value that flips once the ability loads changes their hook count between
  // renders (React error #310). Hooks always run; only the fetch is gated.
  const { ability } = useAbility();
  const canAccessNotifications = ability.can('access', 'notification');
  const unreadCount = notifications.filter((n) => !n.read_at).length;

  const TABS = [
    { label: 'All', value: 1 },
    {
      label: 'Documents',
      value: 2,
      type: [
        'App\\Notifications\\DocumentRecievedNotification',
        'App\\Notifications\\DocumentNotification',
        // "X sent you ... to sign"; its data.link opens the signing page (OL-240).
        'App\\Notifications\\SigningRequestNotification',
      ],
    },
    {
      label: 'Referral Request',
      value: 3,
      type: [
        "App\\Notifications\\ReferralContactNotification",
        "App\\Notifications\\ReferralRequestNotification"
      ]
    },

    // { label: 'Invoice', value: 4, type: 'App\\Notifications\\InvoicePaidNotification' },
  ];

  const handleClose = () => {
    menuTtemRef?.current?.hide?.();
  };

  const getAllNotifications = async () => {
    try {
      const { status, data } = await notificationService.getAllNotifications();
      if (status) {
        pushInitialNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications', error);
      // toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };


  const handleReadNotification = async (id) => {
    try {
      const { status, data } = await notificationService.markNotificationAsRead(id);
      if (status) {
        getAllNotifications()
      }
    } catch (error) {
      console.error('Error fetching notifications', error);
      toast.error("Opps! Something went wrong.", {
        description: 'An error occurred while reading notification. Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReadAllNotifications = async () => {
    try {
      const { status } = await notificationService.markAllNotificationAsRead();
      if (status) {
        getAllNotifications();
      }
    } catch (error) {
      console.error('Error marking all notifications as read', error);
      toast.error("Opps! Something went wrong.", {
        description: 'An error occurred while clearing notifications. Please try again later.',
      });
    }
  };

  const getNotificationRedirectPath = (notification) => {
      // Newer notifications carry their own path (demand-letter approvals, OL-119).
      if (notification?.data?.link) return notification.data.link;
    const contactId = notification?.data?.contact_id || notification?.contact_id;

    switch (notification?.type) {
      case 'App\\Notifications\\ImportCompletedNotification':
        return '/data-import/details';
      case 'App\\Notifications\\ReferralRequestNotification':
        return '/account/home/referral-request';
      case 'App\\Notifications\\ReferralContactNotification':
        return '/account/home/referred-contact-list';
      case 'App\\Notifications\\DocumentRecievedNotification':
      case 'App\\Notifications\\SignedDocumentNotification':
        return '/documents/e-signature-documents';
      case 'App\\Notifications\\DocumentNotification': {
        return contactId ? `/contacts/${contactId}?tab=Files` : null;
      }
      case 'App\\Notifications\\IntakeFormFilledNotification': {
        return contactId ? `/contacts` : null;
      }
      case 'App\\Notifications\\ReminderDueNotification': {
        const type = notification?.data?.remindable_type || notification?.remindable_type;
        const parentId = notification?.data?.remindable_id || notification?.remindable_id;
        if (!type || !parentId) return '/';
        return type === 'contact'
          ? `/contacts/${parentId}?tab=Reminders`
          : `/matters/detail/${parentId}?tab=Reminders`;
      }
      case 'App\\Notifications\\CommentMentionNotification': {
        const type = notification?.data?.commentable_type || notification?.commentable_type;
        const parentId = notification?.data?.commentable_id || notification?.commentable_id;
        if (!type || !parentId) return '/';
        return type === 'contact'
          ? `/contacts/${parentId}?tab=Discussion`
          : `/matters/detail/${parentId}?tab=Discussion`;
      }
      default:
        return null;
    }
  };

  const handleNotificationClick = async (notification) => {
    const redirectPath = getNotificationRedirectPath(notification);

    handleClose();

    if (redirectPath) {
      navigate(redirectPath);
    }
  };


  const testNoti = async () => {
    try {
      const payload = {
        userId: auth?.user?.id,
        message: `Dhakka start notificaiton. hope so it will work :)`,
      }
      const { status, data } = await notificationService.testSocketConnection(payload);
      if (status) {
        getAllNotifications()
      }
    } catch (error) {
      console.error('Error fetching notifications', error);
      toast.error("Opps! Something went wrong.", {
        description: 'An error occurred while reading notifications. Please try again later.',
      });
    }
  }

  useEffect(() => {
    if (!canAccessNotifications) {
      setLoading(false);
      return;
    }
    getAllNotifications();
  }, [canAccessNotifications]);


  const formatDate = (d) => {
    if (!d) return '';
    const parsed = typeof d === 'string' ? parseISO(d) : d;
    return format(parsed, 'MMM dd, yyyy h:mm a');
  };

  const buildTabs = () => (
    <Tabs defaultValue={1}>
      <TabsList className="justify-between px-5 mb-2">
        <div className="flex items-center gap-5">
          {TABS.map(tab => (
            <Tab key={tab.value} value={tab.value}>{tab.label}</Tab>
          ))}
        </div>
      </TabsList>

      {TABS.map(tab => {
        const unread = notifications.filter((n) => !n.read_at);
        const filtered = tab.type
          ? unread.filter((n) =>
            Array.isArray(tab.type) ? tab.type.includes(n.type) : n.type === tab.type
          )
          : unread;

        return (
          <TabPanel key={tab.value} value={tab.value}>
            <div className="text-sm text-center py-2.5">
              {loading ? (
                <p className="text-gray-500">Loading...</p>
              ) : filtered.length === 0 ? (
                <p className="text-gray-500">No notifications</p>
              ) : (
                <>
                  <div className="max-h-[500px] overflow-auto custom-scrollbar space-y-2 px-2.5">
                    {filtered.map((n, idx) => (
                      <div key={n.id ?? idx} className="relative group">
                        <button
                          type="button"
                          onClick={() => handleNotificationClick(n)}
                          className="card w-full px-2 py-0.5 pe-7 text-start space-y-1 text-sm cursor-pointer hover:bg-light transition-colors"
                        >
                          <div>
                            <DefaultTooltip
                              title={n?.data?.message || n.message}
                              placement="top"
                              className="max-w-[438px]"
                            >
                              <span className="line-clamp-2 text-gray-700 group-hover:text-primary">
                                {n?.data?.message || n.message}
                              </span>
                            </DefaultTooltip>
                            {(n?.created_at || n?.time) ? (
                              <span className="mt-1.5 py-1 border-t flex items-center justify-end gap-1 text-xs text-gray-500">
                                <KeenIcon icon="time" className="text-gray-500 text-sm" />
                                {formatDate(n?.created_at || n?.time)}
                              </span>
                            ) : <div></div>}
                          </div>
                        </button>

                        <DefaultTooltip title="Mark as read" placement="top" className="w-fit">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReadNotification(n.id);
                            }}
                            className="absolute top-1 end-1 btn btn-icon btn-xs text-gray-400 hover:text-primary"
                          >
                            <KeenIcon icon="double-check" className="text-base" />
                          </button>
                        </DefaultTooltip>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-4 mx-2 text-end">
                    {/* <button
                      onClick={testNoti}
                      className='btn btn-lin'>
                      Test
                    </button> */}
                    {/* <Link to='/notifications/all' className='btn btn-sm btn-link'>
                      View All
                    </Link> */}

                    {/* <button
                      onClick={handleReadAllNotifications}
                      disabled={filtered.every(n => n.read_at !== null)}
                      className={clsx(
                        'inline-flex items-center gap-1 btn-link btn-xs text-xs justify-center text-primary',
                        filtered.every(n => n.read_at !== null) ? 'cursor-not-allowed' : '',
                      )}>
                      Mark All as Read
                    </button> */}
                  </div>
                </>
              )}

            </div>
          </TabPanel>
        );
      })}
    </Tabs>
  );

  return (
    <MenuSub rootClassName="w-full max-w-[460px]" className="light:border-gray-300">
      <div className="flex items-center justify-between gap-2.5 text-sm text-gray-900 font-semibold px-5 py-2.5 border-b border-b-gray-200">
        Notifications
        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={handleReadAllNotifications}
            disabled={unreadCount === 0}
            className={clsx(
              'btn btn-sm btn-link text-xs font-medium',
              unreadCount === 0
                ? 'text-gray-400 pointer-events-none'
                : 'text-primary'
            )}
          >
            Mark all as read
          </button>
          <button className="btn btn-sm btn-icon btn-light btn-clear shrink-0" onClick={handleClose}>
            <KeenIcon icon="cross" />
          </button>
        </div>
      </div>
      {buildTabs()}
    </MenuSub>
  );
};

export { DropdownNotifications };
